import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { EmptyState, Skeleton, KpiCard } from "@/components/EmptyState";
import {
  ClipboardCheck, Plus, X, CheckCircle2, AlertTriangle, XCircle,
  Sparkles, Boxes, Wrench, FileText, Trash2, Calendar, User as UserIcon,
} from "lucide-react";

const TYPES = [
  { key: "Cleaning", label: "Cleaning", icon: Sparkles, hint: "Tanks, lines, floors, surfaces" },
  { key: "Stock", label: "Stock", icon: Boxes, hint: "Inventory accuracy & counts" },
  { key: "Equipments", label: "Equipments", icon: Wrench, hint: "Calibration & maintenance" },
  { key: "General", label: "General", icon: FileText, hint: "Anything else" },
];

const RESULTS = [
  { key: "pass", label: "Pass", icon: CheckCircle2, cls: "text-emerald-500 bg-emerald-500/10 ring-emerald-500/30" },
  { key: "needs_attention", label: "Needs attention", icon: AlertTriangle, cls: "text-amber-500 bg-amber-500/10 ring-amber-500/30" },
  { key: "fail", label: "Fail", icon: XCircle, cls: "text-destructive bg-destructive/10 ring-destructive/30" },
];

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return String(v); }
}

export default function QualityControl() {
  const [inspections, setInspections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get("/qc"), api.get("/qc/stats"),
      ]);
      setInspections(list.data.inspections || []);
      setStats(st.data);
    } catch (e) { toast.error(extractErr(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDelete = async (i) => {
    if (!window.confirm(`Delete inspection ${i.inspection_number}?`)) return;
    try { await api.delete(`/qc/${i.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  const filtered = filter === "all" ? inspections : inspections.filter((x) => x.type === filter);

  return (
    <AppShell
      title="Quality Control"
      subtitle="Internal inspections — Cleaning, Stock, Equipments, General"
      headerActions={<Button size="sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="qc-create-button"><Plus className="w-4 h-4 mr-1.5" />New inspection</Button>}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Pass rate" value={`${stats?.pass_rate ?? 0}%`} icon={CheckCircle2} accent="bg-emerald-500/10 text-emerald-500" />
        <KpiCard label="Total inspections" value={stats?.total ?? 0} icon={ClipboardCheck} accent="bg-primary/10 text-primary" />
        <KpiCard label="Needs attention" value={stats?.needs_attention ?? 0} icon={AlertTriangle} accent="bg-amber-500/10 text-amber-500" />
        <KpiCard label="Failed" value={stats?.fail ?? 0} icon={XCircle} accent="bg-destructive/10 text-destructive" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button size="sm" variant={filter === "all" ? "default" : "ghost"} onClick={() => setFilter("all")} data-testid="qc-filter-all">All</Button>
        {TYPES.map((t) => (
          <Button key={t.key} size="sm" variant={filter === t.key ? "default" : "ghost"} onClick={() => setFilter(t.key)} data-testid={`qc-filter-${t.key.toLowerCase()}`}>
            <t.icon className="w-3.5 h-3.5 mr-1" />{t.label} ({stats?.by_type?.[t.key] || 0})
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="No inspections yet"
          description="Record internal inspections to verify tanks/equipment are clean and calibrated, stock counts are accurate, and processes are compliant."
          action={<Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="qc-empty-create"><Plus className="w-4 h-4 mr-1.5" />First inspection</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((insp) => {
            const t = TYPES.find((x) => x.key === insp.type) || TYPES[3];
            const r = RESULTS.find((x) => x.key === insp.result) || RESULTS[0];
            return (
              <div key={insp.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`qc-row-${insp.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-mono text-muted-foreground">{insp.inspection_number}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px]">
                        <t.icon className="w-3 h-3" />{insp.type}
                      </span>
                      <span className={`inline-flex items-center gap-1 rounded-full ring-1 px-2 py-0.5 text-[11px] ${r.cls}`}>
                        <r.icon className="w-3 h-3" />{r.label}
                      </span>
                    </div>
                    {insp.area && <div className="font-medium mt-1.5 truncate">{insp.area}</div>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(insp)} data-testid={`qc-delete-${insp.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" />{insp.inspector_name}</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(insp.inspection_date)}</span>
                </div>
                {insp.notes && <div className="mt-2 text-xs text-muted-foreground line-clamp-3">{insp.notes}</div>}
              </div>
            );
          })}
        </div>
      )}

      {open && <InspForm open={open} onClose={() => { setOpen(false); setEditing(null); }} onSaved={load} initial={editing} />}
    </AppShell>
  );
}

function InspForm({ open, onClose, onSaved, initial }) {
  const [form, setForm] = useState({
    type: "Cleaning",
    inspector_name: "",
    inspection_date: new Date().toISOString().slice(0, 16),
    result: "pass",
    area: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initial) setForm({
      type: initial.type, inspector_name: initial.inspector_name,
      inspection_date: initial.inspection_date ? new Date(initial.inspection_date).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
      result: initial.result, area: initial.area || "", notes: initial.notes || "",
    });
    else setForm({
      type: "Cleaning", inspector_name: "", inspection_date: new Date().toISOString().slice(0, 16),
      result: "pass", area: "", notes: "",
    });
  }, [initial, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.inspector_name.trim()) { toast.error("Inspector name is required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        inspection_date: form.inspection_date ? new Date(form.inspection_date).toISOString() : new Date().toISOString(),
      };
      if (initial) await api.put(`/qc/${initial.id}`, payload);
      else await api.post("/qc", payload);
      toast.success(initial ? "Inspection updated" : "Inspection recorded");
      onSaved(); onClose();
    } catch (e) { toast.error(extractErr(e)); }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit inspection" : "New inspection"}</DialogTitle>
          <DialogDescription>
            Quick internal inspection — pick a type, who did it, and the result. Use the area field to note which tank, line, or zone was inspected.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Type *</Label>
            <div className="grid grid-cols-2 gap-2">
              {TYPES.map((t) => {
                const Active = form.type === t.key;
                return (
                  <button
                    key={t.key} type="button"
                    onClick={() => setForm((p) => ({ ...p, type: t.key }))}
                    className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${Active ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-border hover:bg-accent/40"}`}
                    data-testid={`qc-form-type-${t.key.toLowerCase()}`}
                  >
                    <t.icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <div className="text-sm font-medium">{t.label}</div>
                      <div className="text-[11px] text-muted-foreground">{t.hint}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Inspector name *</Label>
              <Input required value={form.inspector_name} onChange={(e) => setForm((p) => ({ ...p, inspector_name: e.target.value }))} placeholder="e.g. Maria Silva" data-testid="qc-form-inspector" />
            </div>
            <div className="space-y-1.5">
              <Label>Date & time</Label>
              <Input type="datetime-local" value={form.inspection_date} onChange={(e) => setForm((p) => ({ ...p, inspection_date: e.target.value }))} data-testid="qc-form-date" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Area / equipment / lot (optional)</Label>
            <Input value={form.area} onChange={(e) => setForm((p) => ({ ...p, area: e.target.value }))} placeholder="e.g. Tank 1, Bottling line, Stockroom A" data-testid="qc-form-area" />
          </div>
          <div className="space-y-1.5">
            <Label>Result *</Label>
            <div className="grid grid-cols-3 gap-2">
              {RESULTS.map((r) => {
                const Active = form.result === r.key;
                return (
                  <button
                    key={r.key} type="button"
                    onClick={() => setForm((p) => ({ ...p, result: r.key }))}
                    className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors ${Active ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-border hover:bg-accent/40"}`}
                    data-testid={`qc-form-result-${r.key}`}
                  >
                    <r.icon className="w-4 h-4" />
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={3} placeholder="Observations, actions taken, follow-ups…" data-testid="qc-form-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="qc-form-submit">{submitting ? "Saving…" : initial ? "Save changes" : "Record inspection"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
